import { apiRequest } from './client'
import type {
  GuardianAlphaCommandResponse,
  GuardianAlphaConfigSaveResponse,
  GuardianAlphaNode,
  GuardianAlphaNodeCreateRequest,
  GuardianAlphaNodeUpdateRequest,
  GuardianAlphaRestartResult,
  GuardianAlphaService,
  GuardianAlphaServiceUpsertRequest,
} from '@/types'

export async function getGuardianAlphaService(
  nodeId: string,
): Promise<GuardianAlphaService> {
  return apiRequest<GuardianAlphaService>(
    `/api/nodes/${nodeId}/guardian-alpha`,
  )
}

export async function upsertGuardianAlphaService(
  nodeId: string,
  body: GuardianAlphaServiceUpsertRequest,
): Promise<GuardianAlphaService> {
  return apiRequest<GuardianAlphaService>(
    `/api/nodes/${nodeId}/guardian-alpha`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
  )
}

export async function createGuardianAlphaNode(
  nodeId: string,
  body: GuardianAlphaNodeCreateRequest,
): Promise<GuardianAlphaNode> {
  return apiRequest<GuardianAlphaNode>(
    `/api/nodes/${nodeId}/guardian-alpha/nodes`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

export async function updateGuardianAlphaNode(
  guardianNodeId: string,
  body: GuardianAlphaNodeUpdateRequest,
): Promise<GuardianAlphaNode> {
  return apiRequest<GuardianAlphaNode>(
    `/api/guardian-alpha/nodes/${guardianNodeId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  )
}

export async function deleteGuardianAlphaNode(
  guardianNodeId: string,
): Promise<{ deleted: boolean }> {
  return apiRequest<{ deleted: boolean }>(
    `/api/guardian-alpha/nodes/${guardianNodeId}`,
    { method: 'DELETE' },
  )
}

export async function saveGuardianAlphaConfig(
  guardianNodeId: string,
  yamlText: string,
): Promise<GuardianAlphaConfigSaveResponse> {
  return apiRequest<GuardianAlphaConfigSaveResponse>(
    `/api/guardian-alpha/nodes/${guardianNodeId}/config`,
    {
      method: 'PUT',
      body: JSON.stringify({ yaml_text: yamlText }),
    },
  )
}

export async function applyGuardianAlphaConfig(
  guardianNodeId: string,
  reason?: string,
): Promise<GuardianAlphaCommandResponse> {
  return apiRequest<GuardianAlphaCommandResponse>(
    `/api/guardian-alpha/nodes/${guardianNodeId}/apply`,
    {
      method: 'POST',
      body: JSON.stringify({ reason: reason || undefined }),
    },
  )
}

export async function readGuardianAlphaFile(
  guardianNodeId: string,
  kind: 'config' | 'input',
): Promise<GuardianAlphaCommandResponse> {
  return apiRequest<GuardianAlphaCommandResponse>(
    `/api/guardian-alpha/nodes/${guardianNodeId}/files/${kind}/read`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  )
}

export interface GuardianAlphaFileReadResult {
  command_id: string
  status: string
  kind: 'config' | 'input'
  yaml_text: string | null
  source_sha256: string | null
  content_sha256: string | null
  error_message: string | null
}

export async function getGuardianAlphaFileReadResult(
  guardianNodeId: string,
  kind: 'config' | 'input',
  commandId: string,
): Promise<GuardianAlphaFileReadResult> {
  return apiRequest<GuardianAlphaFileReadResult>(
    `/api/guardian-alpha/nodes/${guardianNodeId}/files/${kind}/read-result/${commandId}`,
  )
}

export async function saveGuardianAlphaInput(
  guardianNodeId: string,
  yamlText: string,
  sourceSha256?: string,
  readCommandId?: string,
): Promise<GuardianAlphaConfigSaveResponse> {
  const readBinding = sourceSha256 && readCommandId
    ? { source_sha256: sourceSha256, read_command_id: readCommandId }
    : {}
  return apiRequest<GuardianAlphaConfigSaveResponse>(
    `/api/guardian-alpha/nodes/${guardianNodeId}/input`,
    {
      method: 'PUT',
      body: JSON.stringify({
        yaml_text: yamlText,
        ...readBinding,
      }),
    },
  )
}

export async function applyGuardianAlphaInput(
  guardianNodeId: string,
): Promise<GuardianAlphaCommandResponse> {
  return apiRequest<GuardianAlphaCommandResponse>(
    `/api/guardian-alpha/nodes/${guardianNodeId}/input/apply`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  )
}

export async function restartGuardianAlpha(
  nodeId: string,
  reason?: string,
): Promise<GuardianAlphaCommandResponse> {
  return apiRequest<GuardianAlphaCommandResponse>(
    `/api/nodes/${nodeId}/guardian-alpha/restart`,
    {
      method: 'POST',
      body: JSON.stringify({ reason: reason || undefined }),
    },
  )
}

export async function getLatestGuardianAlphaRestartResult(
  nodeId: string,
): Promise<GuardianAlphaRestartResult | null> {
  return apiRequest<GuardianAlphaRestartResult | null>(
    `/api/nodes/${encodeURIComponent(nodeId)}/guardian-alpha/restart-result/latest`,
  )
}
